import Phaser from "phaser";

// ── Debug ─────────────────────────────────────────────────────────────────────
export const drawDebugBox = (scene, x, y, width, height, color = 0xff0000, alpha = 0.3) => {
    const g = scene.add.graphics();
    g.lineStyle(2, color, 1);
    g.strokeRect(x - width / 2, y - height / 2, width, height);
    g.fillStyle(color, alpha);
    g.fillRect(x - width / 2, y - height / 2, width, height);
    return g;
};

export const addStaticRect = (scene, { x, y, width, height }, opts = {}) =>
    scene.matter.add.rectangle(x, y, width, height, { isStatic: true, ...opts });

export const addSensorRect = (scene, rect, label, opts = {}) =>
    addStaticRect(scene, rect, { isSensor: true, label, ...opts });

export const setDepthToY = (go) => go.setDepth(go.y);

// ── Geometry ──────────────────────────────────────────────────────────────────
export const aabbFromPolygon = (polygon) => {
    const xs = polygon.map(([x]) => x);
    const ys = polygon.map(([, y]) => y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const width = maxX - minX;
    const height = maxY - minY;
    return { x: minX + width / 2, y: minY + height / 2, width, height, minX, maxX, minY, maxY };
};

export const pointInPoly = (x, y, poly) => {
    // Ray-casting
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const [xi, yi] = poly[i], [xj, yj] = poly[j];
        const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / ((yj - yi) || 1e-9) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
};

export const distPointToSegment = (px, py, ax, ay, bx, by) => {
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy || 1e-9;
    let t = ((px - ax) * dx + (py - ay) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const cx = ax + t * dx, cy = ay + t * dy;
    const ddx = px - cx, ddy = py - cy;
    return Math.hypot(ddx, ddy);
};

export const minDistanceToPolyEdges = (px, py, poly) => {
    let minD = Infinity;
    for (let i = 0; i < poly.length; i++) {
        const a = poly[i], b = poly[(i + 1) % poly.length];
        const d = distPointToSegment(px, py, a[0], a[1], b[0], b[1]);
        if (d < minD) minD = d;
    }
    return minD;
};

// ── Collision nav helpers ─────────────────────────────────────────────────────
export const buildObstacleIndex = (buildings = []) => {
    const blockers = buildings.filter(b => !b.name && Array.isArray(b.polygon));
    return blockers.map(b => {
        const { minX, maxX, minY, maxY } = aabbFromPolygon(b.polygon);
        return { poly: b.polygon, aabb: { minX, maxX, minY, maxY } };
    });
};

export const isWalkable = (obstacles, x, y, radius = 20, minClearance = 32) => {
    if (!obstacles?.length) return true;

    for (const ob of obstacles) {
        const { minX, maxX, minY, maxY } = ob.aabb;
        if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
            if (pointInPoly(x, y, ob.poly)) return false;
        }
    }

    const required = radius + minClearance;
    for (const ob of obstacles) {
        const dx = Math.max(ob.aabb.minX - x, 0, x - ob.aabb.maxX);
        const dy = Math.max(ob.aabb.minY - y, 0, y - ob.aabb.maxY);
        const approx = Math.hypot(dx, dy);
        if (approx > required + 24) continue;

        const edgeD = minDistanceToPolyEdges(x, y, ob.poly);
        if (edgeD < required) return false;
    }
    return true;
};

export const pickRandomWalkablePoint = (
    obstacles,
    bounds,
    minClearance = 32,
    margin = 40,
    maxAttempts = 120,
    bodyRadius = 20
) => {
    const minX = bounds.x + margin;
    const maxX = bounds.x + bounds.width - margin;
    const minY = bounds.y + margin;
    const maxY = bounds.y + bounds.height - margin;

    for (let i = 0; i < maxAttempts; i++) {
        const x = Phaser.Math.Between(minX, maxX);
        const y = Phaser.Math.Between(minY, maxY);
        if (isWalkable(obstacles, x, y, bodyRadius, minClearance)) {
            return { x, y };
        }
    }

    // sensible fallback: center of the bounds
    return {
        x: bounds.x + bounds.width * 0.5,
        y: bounds.y + bounds.height * 0.5,
    };
};


// Doors in your collisions JSON use opposite corners [0] and [2]
export const getDoorCenter = (buildings, id) => {
    const b = buildings.find(x => x.id === id);
    if (!b) return null;
    const [x0, y0] = b.polygon[0], [x1, y1] = b.polygon[2];
    return { x: (x0 + x1) / 2, y: (y0 + y1) / 2, w: x1 - x0, h: y1 - y0 };
};

// returns a tight AABB around all building polys (shrunk by pad)
export const computePlayableRect = (buildings = [], pad = 32) => {
    if (!buildings?.length) return { x: 0, y: 0, width: 1024, height: 1024 };

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const b of buildings) {
        for (const [x, y] of b.polygon) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        }
    }
    // shrink in a little so spawns don’t hug the outside wall
    minX += pad; minY += pad; maxX -= pad; maxY -= pad;

    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

